import {
  Actor,
  CompanyId,
  LoanId,
  reviveWorkflowInstanceStatus,
  Temporal,
  WorkflowDefinitionId,
  WorkflowInstanceId,
  WorkflowInstanceStatus,
} from '@willow/types-iso';

import { StepType } from '../definitions/steps/StepDefinition';
import { WorkflowDefinition, WorkflowDefinitionSansSteps } from '../definitions/WorkflowDefinition';
import { HydratedStepInstance } from './HydratedStepInstance';
import { ClosureReason, SerializedWorkflowInstance } from './SerializedWorkflowInstance';
import { transformSerializedClosureReasons } from './transformSerializedClosureReasons';

export class HydratedWorkflowInstance implements Omit<SerializedWorkflowInstance, 'serializedSteps'> {
  id: WorkflowInstanceId;

  companyId: CompanyId;

  loanId: LoanId;

  launchTime: Temporal.ZonedDateTime;

  timeZone: Temporal.TimeZoneProtocol;

  steps: HydratedStepInstance<StepType>[];

  definition: WorkflowDefinitionSansSteps;

  launchActor: Actor;

  definitionId: WorkflowDefinitionId;

  definitionVersion: number;

  closureReasons: ClosureReason[];

  statusHistory: WorkflowInstanceStatus[];

  constructor(
    public readonly workflowInstance: SerializedWorkflowInstance,
    { steps: stepDefinitions, ...workflowDefinition }: WorkflowDefinition,
  ) {
    this.id = workflowInstance.id;
    this.companyId = workflowInstance.companyId;
    this.loanId = workflowInstance.loanId;
    this.launchTime = Temporal.ZonedDateTime.from(workflowInstance.launchTime);
    this.timeZone = Temporal.TimeZone.from(workflowInstance.timeZone);
    this.steps = workflowInstance.serializedSteps.map((step, i) => new HydratedStepInstance(step, stepDefinitions[i]));
    this.definition = workflowDefinition;
    this.launchActor = workflowInstance.launchActor;
    this.definitionId = workflowInstance.definitionId;
    this.definitionVersion = workflowInstance.definitionVersion;
    this.closureReasons = transformSerializedClosureReasons(workflowInstance.closureReasons ?? []);
    this.statusHistory = workflowInstance.statusHistory.map((status) => reviveWorkflowInstanceStatus(status));
  }

  public get status() {
    return this.statusHistory[this.statusHistory.length - 1];
  }

  public get statusLastUpdated() {
    return this.statusHistory[this.statusHistory.length - 1].timestamp;
  }

  public get statusComment() {
    return this.statusHistory[this.statusHistory.length - 1].comment;
  }

  public get statusActor() {
    return this.statusHistory[this.statusHistory.length - 1].actor;
  }

  public getClosureReasonLabel(reasonId: string): string | undefined {
    return this.closureReasons.find((reason) => reason.reasonId === reasonId)?.label;
  }
}
